import { useEffect, useState } from 'react'
import { Search, Plus, Pencil, Loader2 } from 'lucide-react'
import { useAdmin } from '../../hooks/use-admin'
import { useAuth } from '../../hooks/use-auth'
import { get } from '../../helper/network'
import API from '../../helper/api'
import { useI18n } from '../../i18n'
import type { EnterpriseConfigOverride, EnterpriseConfigOverrideRequest, ConfigValueType } from '../../types'

interface EnterpriseItem {
  id: string
  name: string
  logoUrl?: string
  industryType?: string
}

const valueTypes: ConfigValueType[] = ['STRING', 'NUMBER', 'BOOLEAN', 'JSON']

export function EnterpriseConfigSection() {
  const { enterpriseOverrides, loading, fetchEnterpriseOverrides, saveEnterpriseOverride } = useAdmin()
  const { t } = useI18n()

  const [enterprises, setEnterprises] = useState<EnterpriseItem[]>([])
  const [selectedId, setSelectedId] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingOverride, setEditingOverride] = useState<EnterpriseConfigOverride | null>(null)

  // Form state
  const [form, setForm] = useState<EnterpriseConfigOverrideRequest>({
    configKey: '',
    configValue: '',
    configName: '',
    configGroup: '',
    valueType: 'STRING',
    description: '',
  })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    get(API.GET_ENTERPRISE_LIST).then((res: any) => {
      if (res?.code === 200 && res.data) {
        setEnterprises(Array.isArray(res.data) ? res.data : [])
      }
    })
  }, [])

  useEffect(() => {
    if (selectedId) {
      fetchEnterpriseOverrides(selectedId)
    }
  }, [selectedId, fetchEnterpriseOverrides])

  const filtered = enterprises.filter((e) =>
    e.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const selectedEnterprise = enterprises.find((e) => e.id === selectedId)

  const openCreate = () => {
    setEditingOverride(null)
    setForm({ configKey: '', configValue: '', configName: '', configGroup: '', valueType: 'STRING', description: '' })
    setDialogOpen(true)
  }

  const openEdit = (item: EnterpriseConfigOverride) => {
    setEditingOverride(item)
    setForm({
      configKey: item.configKey,
      configValue: item.configValue,
      configName: item.configName || '',
      configGroup: '',
      valueType: item.valueType,
      description: item.description,
    })
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (!selectedId || !form.configKey || !form.configValue) return
    setSubmitting(true)
    const ok = await saveEnterpriseOverride(selectedId, form)
    setSubmitting(false)
    if (ok) {
      setDialogOpen(false)
      fetchEnterpriseOverrides(selectedId)
    }
  }

  return (
    <div className="space-y-4">
      {/* Enterprise selector */}
      <div>
        <label className="block text-[11px] text-gray-400 mb-1.5">
          {t('选择企业', 'Select Enterprise')}
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('搜索企业名称', 'Search enterprise')}
            className="w-full h-9 text-sm pl-9 pr-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none"
          />
        </div>
        {searchQuery && (
          <div className="mt-1 max-h-40 overflow-y-auto rounded-xl border border-black/[0.04] bg-white">
            {filtered.length === 0 ? (
              <div className="text-sm text-gray-400 p-3 text-center">{t('无匹配', 'No match')}</div>
            ) : (
              filtered.map((e) => (
                <button
                  key={e.id}
                  onClick={() => { setSelectedId(e.id); setSearchQuery('') }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left hover:bg-black/[0.02] transition-colors ${
                    selectedId === e.id ? 'bg-orange-50 text-orange-600' : 'text-[#1a1a1a]'
                  }`}
                >
                  {e.name}
                  {e.industryType && (
                    <span className="text-[10px] text-gray-400">{e.industryType}</span>
                  )}
                </button>
              ))
            )}
          </div>
        )}
        {selectedEnterprise && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-xl bg-orange-50 text-sm">
            <span className="text-orange-600 font-medium">{selectedEnterprise.name}</span>
            <button
              onClick={() => setSelectedId('')}
              className="text-[11px] text-gray-400 hover:text-[#1a1a1a]"
            >
              {t('切换', 'Change')}
            </button>
          </div>
        )}
      </div>

      {/* Overrides list */}
      {selectedId && (
        <>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-[#1a1a1a]">
              {t('配置覆盖', 'Config Overrides')}
            </h3>
            <button
              onClick={openCreate}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-xl bg-orange-500 text-white hover:bg-orange-600 transition-all"
            >
              <Plus className="h-3.5 w-3.5" />
              {t('新增', 'Add')}
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
            </div>
          ) : enterpriseOverrides.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">
              {t('该企业暂无配置覆盖', 'No overrides for this enterprise')}
            </div>
          ) : (
            <div className="space-y-2">
              {enterpriseOverrides.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-3 rounded-xl bg-white border border-black/[0.04] group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[13px] text-[#1a1a1a] font-medium truncate">
                        {item.configName || item.configKey}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-black/[0.04] text-gray-400 font-mono">
                        {item.configKey}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-black/[0.04] text-gray-400 font-mono">
                        {item.valueType}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 font-mono truncate">
                      {item.configValue}
                    </div>
                    {item.description && (
                      <div className="text-[11px] text-gray-400 mt-0.5 truncate">
                        {item.description}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => openEdit(item)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all opacity-0 group-hover:opacity-100"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Add/Edit dialog */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setDialogOpen(false)} />
          <div className="relative bg-white rounded-2xl border border-black/[0.06] shadow-xl w-full max-w-md mx-4 p-5">
            <h3 className="text-sm font-semibold text-[#1a1a1a] mb-4">
              {editingOverride ? t('编辑覆盖', 'Edit Override') : t('新增覆盖', 'Add Override')}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] text-gray-400 mb-1">configKey *</label>
                <input
                  value={form.configKey}
                  onChange={(e) => setForm((f) => ({ ...f, configKey: e.target.value }))}
                  disabled={!!editingOverride}
                  className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none disabled:opacity-50"
                  placeholder="plan.expire.days"
                />
              </div>
              <div>
                <label className="block text-[11px] text-gray-400 mb-1">configValue *</label>
                <textarea
                  value={form.configValue}
                  onChange={(e) => setForm((f) => ({ ...f, configValue: e.target.value }))}
                  rows={2}
                  className="w-full text-sm px-3 py-2 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none resize-none"
                  placeholder="30"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">valueType</label>
                  <select
                    value={form.valueType}
                    onChange={(e) => setForm((f) => ({ ...f, valueType: e.target.value as ConfigValueType }))}
                    className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none"
                  >
                    {valueTypes.map((vt) => (
                      <option key={vt} value={vt}>{vt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">{t('描述', 'Description')}</label>
                  <input
                    value={form.description ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setDialogOpen(false)}
                className="px-4 py-2 text-[13px] rounded-xl bg-black/[0.04] text-gray-400 hover:text-[#1a1a1a] transition-all"
              >
                {t('取消', 'Cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !form.configKey || !form.configValue}
                className="px-4 py-2 text-[13px] rounded-xl bg-orange-500 text-white hover:bg-orange-600 transition-all disabled:opacity-50"
              >
                {submitting ? '...' : t('确认', 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
